// src/services/registrationService.ts
import api from './api';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface RegistrationResponse {
  id: string;
  confirmationCode: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'attended';
  paymentStatus: 'pending' | 'paid' | 'refunded' | 'failed';
  participantName: string;
  participantEmail: string;
  eventTitle: string;
  ticketName: string;
  amountPaid: number;
  createdAt: string;
}

// Define um tipo reduzido para inscrição gratuita
type FreeRegistrationDTO = {
  name: string;
  email: string;
  phone: string;
  ticketId: string;
  quantity: number;
  customFields: Record<string, string | boolean | string[]>;
  ref?: string;
  legalAccepted?: { key: string; version: string }[];
};

type PendingRegistrationDTO = {
  name: string;
  email: string;
  phone: string;
  ticketId: string;
  quantity: number;
  customFields: Record<string, string | boolean | string[]>;
  cpf?: string;
  amountPaid: number; // 👈 obrigatório para pending
  ref?: string;
  legalAccepted?: { key: string; version: string }[];
  meta?: { fbp?: string; fbc?: string; eventSourceUrl?: string };
};

export const registrationService = {
  // Obter todas as inscrições
  getAll: async () => {
    try {
      const response = await api.get<ApiResponse<RegistrationResponse[]>>('/registrations');
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar inscrições:', error);
      throw error;
    }
  },

  // Registrar gratuitamente em um evento
  registerFree: async (eventId: string, data: FreeRegistrationDTO) => {
    try {
      const response = await api.post<ApiResponse<RegistrationResponse>>('/registration/free', {
        eventId,
        ...data,
      });
      return response.data;
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Erro ao registrar inscrição gratuita';
      throw new Error(message);
    }
  },

  createPending: async (eventId: string, data: PendingRegistrationDTO) => {
    try {
      const response = await api.post<ApiResponse<RegistrationResponse>>('/registration/pending', {
        eventId,
        ...data,
      });

      
      return response.data;
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Erro ao criar inscrição pendente';
      throw new Error(message);
    }
  },

  createOrder: async (data: {
    eventId: string;
    attendees: Array<{
      ticketId: string;
      name: string;
      email: string;
      phone?: string;
      customFields?: Record<string, unknown>;
    }>;
    buyer?: { name?: string; email?: string; phone?: string; document?: string };
    refCode?: string;
    legalAccepted?: { key: string; version: string }[];
    meta?: { fbp?: string; fbc?: string; eventSourceUrl?: string };
  }) => {
    try {
      const response = await api.post<
        ApiResponse<{
          orderId: string;
          id: string;
          primaryRegistrationId: string;
          registrationIds: string[];
          amountPaid: number;
          paymentStatus: string;
          isFree: boolean;
        }>
      >('/orders/pending', data);
      return response.data;
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Erro ao criar pedido';
      throw new Error(message);
    }
  },
  

  updateAfterPayment: async (
    registrationId: string,
    payload: {
      paymentStatus: 'paid' | 'refunded' | 'failed';
      amountPaid: number;
      transactionId?: string;
    }
  ) => {
    try {
      const response = await api.patch<ApiResponse<RegistrationResponse>>(`/registration/${registrationId}/payment`, payload);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar pagamento da inscrição:', error);
      throw error;
    }
  },

  checkStatus: async (registrationId: string) => {
    try {
      const response = await api.get<ApiResponse<{ 
        paymentStatus: 'pending' | 'paid' | 'refunded' | 'failed'; 
        status: 'pending' | 'confirmed' | 'cancelled' | 'attended';
        updatedAt: string;
        checkInCode?: string;         // 👈 novo campo
        qrCodeBase64?: string;        // 👈 novo campo
        checkedIn?: boolean;          // 👈 opcional (se quiser exibir)
        checkedInAt?: string;         // 👈 opcional (para histórico)
      }>>(`/registration/${registrationId}/status`);
      
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar status da inscrição:', error);
      throw error;
    }
  },

  downloadTicketPDF: async (registrationId: string, fileName: string) => {
    const response = await api.get(`/registration/${registrationId}/download`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  
  getById: async (registrationId: string) => {
    try {
      const response = await api.get<ApiResponse<RegistrationResponse>>(`/registration/${registrationId}`);
      console.log(response, 'registrationId');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados da inscrição:', error);
      throw error;
    }
  },

  pay: async (
    registrationId: string,
    payload: {
      cardTokenId: string;
      paymentMethodId: string;
      issuerId: string | null;
      installments: number;
      payer: {
        email: string;
        identification: {
          type: string; // Ex: "CPF"
          number: string;
        };
      };
    }
  ) => {
    try {
      const response = await api.post<ApiResponse<RegistrationResponse>>(
        `/registration/${registrationId}/pay`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Erro ao pagar inscrição:', error);
      throw error;
    }
  },

  checkSlugAvailability: async (slug: string) => {
  try {
    const token = localStorage.getItem('token');
    console.log('[checkSlugAvailability] Token:', token);
    console.log('[checkSlugAvailability] Slug:', slug);

    const response = await api.get<{ available: boolean }>(
      `/registration/slug/${slug}/check`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log('[checkSlugAvailability] Response data:', response.data);

    return response.data; // { available: true/false }
  } catch (error: any) {
    console.error('[checkSlugAvailability] Erro ao verificar slug:', error?.response?.data || error.message);
    throw error;
  }
}




};

